import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";

const UploadedBins = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [file, setFile] = useState(null);
  const [bins, setBins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { current, total }

  // Fetch products
  useEffect(() => {
    axios
      .get("https://crm-server-htk6.vercel.app/api/products")
      .then((res) => setProducts(res.data))
      .catch((err) => console.error("❌ Error fetching products:", err));
  }, []);

  // Fetch bins for selected product
  useEffect(() => {
    if (!selectedProduct) {
      setBins([]);
      return;
    }
    setLoading(true);
    axios
      .get(`https://crm-server-htk6.vercel.app/api/bins/${selectedProduct}`)
      .then((res) => setBins(res.data))
      .catch((err) => console.error("❌ Error fetching bins:", err))
      .finally(() => setLoading(false));
  }, [selectedProduct]);

  const handleFileChange = (e) => setFile(e.target.files[0]);

  // chunk helper
  const chunkArray = (arr, size) =>
    Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
      arr.slice(i * size, i * size + size)
    );

  const normalizeHeaderCell = (v) =>
    String(v ?? "")
      .replace(/[^a-z0-9]/gi, "") // remove spaces, underscores, hyphens, etc.
      .toLowerCase();

  const handleUpload = async () => {
    if (!file || !selectedProduct) {
      return alert("Please select a product and file first");
    }

    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];

        // Read sheet as array-of-arrays so we can control header handling
        const sheetData = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: "",
        });

        if (!sheetData || sheetData.length <= 1) {
          return alert("No data rows found in the Excel file.");
        }

        // First row = header row (we will SKIP it as requested)
        const headerRowRaw = (sheetData[0] || []).map((c) => String(c || ""));
        const headerRow = headerRowRaw.map(normalizeHeaderCell);

        // Try to detect StartRange and EndRange column indices from header (flexible)
        let startIdx = headerRow.findIndex((h) =>
          /(start.*range|startrange|^start$)/.test(h)
        );
        let endIdx = headerRow.findIndex((h) =>
          /(end.*range|endrange|^end$)/.test(h)
        );

        // If detection failed, we'll fall back to using first and second columns (0 and 1),
        // and we'll leave the header row untouched (i.e. we still skip it and read from row 2)
        let usedHeaderDetection = true;
        if (startIdx === -1 || endIdx === -1) {
          usedHeaderDetection = false;
          startIdx = 0;
          endIdx = 1;
          console.warn(
            "Header detection failed for StartRange/EndRange. Falling back to columns 1 and 2."
          );
        }

        // Always skip the first row and treat remaining rows as data (user request)
        const dataRows = sheetData.slice(1);

        // Map rows to objects with required keys
        const mappedRows = dataRows
          .map((rowArr, rowIndex) => {
            const startVal = rowArr[startIdx] ?? "";
            const endVal = rowArr[endIdx] ?? "";
            return {
              StartRange: String(startVal).trim(),
              EndRange: String(endVal).trim(),
              ProductName: selectedProduct,
              _rowNumber: rowIndex + 2, // helpful for debugging (excel row number)
            };
          })
          // remove empty rows (both start and end empty)
          .filter((r) => (r.StartRange && r.EndRange) || (r.StartRange || r.EndRange));

        if (mappedRows.length === 0) {
          return alert(
            "No valid rows found after skipping the first header row. Make sure data starts from row 2."
          );
        }

        // Ensure each row has BOTH StartRange and EndRange
        const badRow = mappedRows.find((r) => !r.StartRange || !r.EndRange);
        if (badRow) {
          return alert(
            `Row ${badRow._rowNumber} is missing StartRange or EndRange. Please fix the file or remove that row.`
          );
        }

        // Check duplicates within uploaded file
        const startValues = mappedRows.map((r) => r.StartRange);
        const dupInFile = startValues.find(
          (v, i) => v && startValues.indexOf(v) !== i
        );
        if (dupInFile) {
          return alert(`Duplicate StartRange found in Excel file: ${dupInFile}`);
        }

        // Check against existing bins (prevent duplicate StartRange)
        const existingStartRanges = bins.map((b) => String(b.StartRange));
        const dupWithExisting = mappedRows.find((r) =>
          existingStartRanges.includes(r.StartRange)
        );
        if (dupWithExisting) {
          return alert(
            `StartRange ${dupWithExisting.StartRange} already exists for this product.`
          );
        }

        // All validations passed -> upload in chunks
        setLoading(true);

        const CHUNK_SIZE = 1000; // tweak if needed
        const chunks = chunkArray(mappedRows, CHUNK_SIZE);

        for (let i = 0; i < chunks.length; i++) {
          setUploadProgress({ current: i + 1, total: chunks.length });
          await axios.post("https://crm-server-htk6.vercel.app/api/bins", {
            productName: selectedProduct,
            bins: chunks[i].map(({ _rowNumber, ...keep }) => keep), // remove helper field before sending
          });
          console.log(`Uploaded chunk ${i + 1}/${chunks.length}`);
        }

        // Refresh bins from backend after upload
        const res = await axios.get(
          `https://crm-server-htk6.vercel.app/api/bins/${selectedProduct}`
        );
        setBins(res.data);

        alert(
          `✅ Uploaded ${mappedRows.length} rows in ${chunks.length} chunk(s). Header detection used: ${usedHeaderDetection}`
        );
      } catch (err) {
        console.error("❌ Error processing/uploading:", err);
        alert(err.response?.data?.error || "Upload failed. See console for details.");
      } finally {
        setLoading(false);
        setUploadProgress(null);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleDeleteAll = async () => {
    if (!selectedProduct) return alert("Please select a product first");
    if (!window.confirm("Are you sure you want to delete all bins?")) return;

    try {
      setLoading(true);
      await axios.delete(
        `https://crm-server-htk6.vercel.app/api/bins/${selectedProduct}`
      );
      setBins([]);
      alert("✅ All bins deleted successfully");
    } catch (err) {
      console.error("❌ Error deleting bins:", err);
      alert("Failed to delete bins");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (bins.length === 0) return alert("No bins to export");
    const worksheet = XLSX.utils.json_to_sheet(bins);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Bins");
    XLSX.writeFile(workbook, `${selectedProduct}_bins.xlsx`);
  };

  return (
    <div className="page">
      <h2>Uploaded Bins</h2>

      <div className="form-container">
        <select
          value={selectedProduct}
          onChange={(e) => setSelectedProduct(e.target.value)}
          disabled={loading}
        >
          <option value="">Select Product</option>
          {products.map((p) => (
            <option key={p._id} value={p.productName}>
              {p.productName}
            </option>
          ))}
        </select>
      </div>

      <div className="actions">
        <input
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileChange}
          disabled={loading}
        />
        <button className="bins-btn" onClick={handleUpload} disabled={loading}>
          Upload
        </button>
        <button
          className="bins-btn"
          onClick={handleDeleteAll}
          disabled={loading}
        >
          Delete All Bins
        </button>
        <button className="bins-btn" onClick={handleExport} disabled={loading}>
          Export to Excel
        </button>
      </div>

      <h3 className="list-title">Bins for {selectedProduct}</h3>
      <div
        className="table-container"
        style={{ maxHeight: "400px", overflowY: "auto" }}
      >
        <table className="user-table">
          <thead>
            <tr>
              <th>Start Range</th>
              <th>End Range</th>
              <th>Product Name</th>
            </tr>
          </thead>
          <tbody>
            {bins.length === 0 ? (
              <tr>
                <td colSpan="3" style={{ textAlign: "center" }}>
                  No bins uploaded yet
                </td>
              </tr>
            ) : (
              bins.map((b, index) => (
                <tr key={index}>
                  <td>{b.StartRange}</td>
                  <td>{b.EndRange}</td>
                  <td>{b.ProductName}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="spinner" />
          {uploadProgress ? (
            <div>
              ⏳ Uploading chunk {uploadProgress.current} of{" "}
              {uploadProgress.total}...
            </div>
          ) : (
            <div>⏳ Processing / Uploading Bins ...</div>
          )}
        </div>
      )}
    </div>
  );
};

export default UploadedBins;
